/* eslint-disable no-console */
import { ApolloClient, InMemoryCache, ApolloLink } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { onError } from '@apollo/client/link/error';
import createUploadLink from 'apollo-upload-client/public/createUploadLink.js';
import introspectionQueryResultData from './fragmentTypes.json';
import * as Sentry from '@sentry/browser';

const authLink = new ApolloLink((operation, forward) => {
  const TOKEN = document.querySelector('meta[name="jwt"]')?.getAttribute('content');
  const CSRF = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      Authorization: TOKEN,
      'X-CSRF-TOKEN': CSRF,
    },
  }));

  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.warn(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
    Sentry.captureException(networkError);
  }
});

const uploadLink = createUploadLink({
  uri: `/${I18n.locale}/api/graphql`,
  credentials: 'include',
});

const retryLink = new RetryLink({
  attempts: {
    max: 5,
    retryIf: (error) => !!error && error.statusCode !== 401 && error.statusCode !== 403,
  },
});

// CACHE
function getUserIdFromJwt() {
  try {
    const token = document.querySelector('meta[name="jwt"]')?.getAttribute('content');
    if (!token) return 'anonymous';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.user_id ?? 'anonymous';
  } catch {
    return 'anonymous';
  }
}

export const SESSION_KEY = `dokeos_apollo_cache_${getUserIdFromJwt()}`;

// fragmentTypes.json is a raw introspection dump, not the { InterfaceName: [Type, ...] } shape
// InMemoryCache expects, so introspectionQueryResultData.possibleTypes is always undefined --
// existing wiring that predates this and stays as-is (a separate, unscoped problem). Hand-written
// here instead, for the one interface that needs it: without this, the inline fragment on
// ModuleContentStat in STATISTICS_MODULE_CONTENT_QUERY would not match against the cache.
const possibleTypes = {
  ...(introspectionQueryResultData.possibleTypes || {}),
  ModuleContentStat: ['AssessmentQuestionStat', 'SurveyQuestionStat'],
};

const cache = new InMemoryCache({
  possibleTypes,
  typePolicies: {
    SurveyChoiceStat: { keyFields: false },
    SurveyTableLabel: { keyFields: false },
    SurveyTableCellStat: { keyFields: false },
    SurveyChoiceRespondent: { keyFields: false },
    QuestionChoiceStat: { keyFields: false },
    SurveyQuestionChoiceStat: { keyFields: false },
    SurveyTableRowStat: { keyFields: false },
    SurveyTableColumnStat: { keyFields: false },
  },
});

try {
  const stored = sessionStorage.getItem(SESSION_KEY);
  if (stored) {
    const parsed = JSON.parse(stored);
    // Always fetch fresh pending surveys — they change when participations are deleted server-side
    if (parsed.ROOT_QUERY) {
      delete parsed.ROOT_QUERY['pending_satisfaction_surveys'];
    }
    cache.restore(parsed);
  }
} catch {
  /* ignore parse/access errors */
}

const link = ApolloLink.from([retryLink, authLink, errorLink, uploadLink]);

// CLIENT
const apiClient = new ApolloClient({
  link,
  cache,
});

// for debugging
if (process.env.NODE_ENV === 'development') {
  window.__APOLLO_CLIENT__ = apiClient;
}

export default function getApiClient() {
  return apiClient;
}
